import React, { useState, useEffect } from "react";
import { Mail, Check, X, ExternalLink } from "lucide-react";
import { useSupabase } from "../../contexts/SupabaseContext";
import { useBusiness } from "../../contexts/BusinessContext";

interface GmailConnection {
  id: string;
  email: string;
  connected_at: string;
  is_active: boolean;
}

export function GmailSettings() {
  const supabase = useSupabase();
  const { businessId } = useBusiness();
  const [gmailConnections, setGmailConnections] = useState<GmailConnection[]>([]);
  const [isConnecting, setIsConnecting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadGmailConnections();
  }, [businessId]);

  const loadGmailConnections = async () => {
    if (!businessId) return;
    
    try {
      const { data, error } = await supabase
        .from("gmail_connections")
        .select("*")
        .eq("business_id", businessId)
        .eq("is_active", true)
        .order("connected_at", { ascending: false });

      if (error) throw error;
      setGmailConnections(data || []);
    } catch (error) {
      console.error("Error loading Gmail connections:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnectGmail = async () => {
    setIsConnecting(true);
    
    try {
      // Use Vite environment variables
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const redirectUrl = `${supabaseUrl}/functions/v1/gmail-oauth-callback`;
      const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
      
      console.log("Google Client ID:", clientId ? "Set" : "Not set");
      console.log("Supabase URL:", supabaseUrl);
      console.log("Redirect URL:", redirectUrl);
      
      if (!clientId) {
        alert("Google Client ID not configured. Please add VITE_GOOGLE_CLIENT_ID to your environment variables.");
        throw new Error("Google Client ID not configured");
      }

      if (!supabaseUrl) {
        alert("Supabase URL not configured. Please add VITE_SUPABASE_URL to your environment variables.");
        throw new Error("Supabase URL not configured");
      }

      const scope = encodeURIComponent([
        "https://www.googleapis.com/auth/gmail.readonly",
        "https://www.googleapis.com/auth/gmail.send",
        "https://www.googleapis.com/auth/gmail.modify",
        "https://www.googleapis.com/auth/userinfo.email",
        "https://www.googleapis.com/auth/userinfo.profile"
      ].join(" "));

      const state = btoa(JSON.stringify({ 
        businessId, 
        returnUrl: window.location.href 
      }));
      
      const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
        `client_id=${clientId}&` +
        `redirect_uri=${encodeURIComponent(redirectUrl)}&` +
        `response_type=code&` +
        `scope=${scope}&` +
        `access_type=offline&` +
        `prompt=consent&` +
        `state=${encodeURIComponent(state)}`;

      console.log("Redirecting to:", authUrl);
      
      window.location.href = authUrl;
      
    } catch (error) {
      console.error("Error initiating Gmail connection:", error);
      setIsConnecting(false);
    }
  };

  const handleDisconnectGmail = async (connectionId: string) => {
    try {
      const { error } = await supabase
        .from("gmail_connections")
        .update({ is_active: false })
        .eq("id", connectionId);

      if (error) throw error;
      
      // Reload connections
      loadGmailConnections();
    } catch (error) {
      console.error("Error disconnecting Gmail:", error);
    }
  };

  if (loading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow">
      <div className="flex items-center mb-4">
        <Mail className="h-6 w-6 text-red-500 mr-3" />
        <h3 className="text-lg font-semibold">Gmail Integration</h3>
      </div>
      
      <p className="text-gray-600 mb-6">
        Connect your Gmail account to send and receive emails directly from your booking app.
        This allows you to manage all customer communications in one place.
      </p>

      {/* Connected Accounts */}
      {gmailConnections.length > 0 && (
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Connected Accounts</h4>
          <div className="space-y-3">
            {gmailConnections.map((connection) => (
              <div
                key={connection.id}
                className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg"
              >
                <div className="flex items-center">
                  <Check className="h-5 w-5 text-green-500 mr-3" />
                  <div>
                    <p className="font-medium text-gray-900">{connection.email}</p>
                    <p className="text-sm text-gray-500">
                      Connected on {new Date(connection.connected_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleDisconnectGmail(connection.id)}
                  className="text-red-600 hover:text-red-700 text-sm font-medium"
                >
                  Disconnect
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Connect New Account */}
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-medium text-gray-700">Add Gmail Account</h4>
          <p className="text-sm text-gray-500">
            Connect a new Gmail account to expand your email capabilities
          </p>
        </div>
        <button
          onClick={handleConnectGmail}
          disabled={isConnecting}
          className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isConnecting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Connecting...
            </>
          ) : (
            <>
              <ExternalLink className="h-4 w-4 mr-2" />
              Connect Gmail
            </>
          )}
        </button>
      </div>

      {/* Features List */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Features</h4>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• Send emails directly from the booking app</li>
          <li>• Receive and manage customer email replies</li>
          <li>• Automatic email threading with customer conversations</li>
          <li>• Email templates for common booking scenarios</li>
          <li>• Real-time email notifications</li>
        </ul>
      </div>
    </div>
  );
} 